import type { EventCapturer } from '#src/analytics/analytics_event.ts';
import { useTrackEventOnce } from '#src/analytics/react/use_track_event_once.ts';
import {
  GroupNameConfirmEvent,
  GroupNameWaitViewEvent,
} from '#src/groups/analytics/group_analytics_events.ts';
import type { DeviceMemory } from '#src/groups/device_memory/device_memory.ts';
import { formatWait } from '#src/groups/device_memory/device_memory.ts';
import lockNameImage from '#src/pages/group/confirm_name_card/assets/lock_name.webp';
import {
  useCountdown,
  useNameChangeWait,
} from '#src/pages/group/confirm_name_card/use_name_change_wait.ts';
import { DisplayParticipantName } from '#src/ui/presentation/display_participant_name.tsx';
import type { GroupParticipant } from '@packages/contracts/group';
import { exists } from '@packages/primitives/exists';
import { Button } from '@packages/ui/base/button/button';
import { Card } from '@packages/ui/base/card/card';

export interface ConfirmNameCardProps {
  captureEvent: EventCapturer;
  participant: GroupParticipant;
  deviceMemory: DeviceMemory;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirms a name before showing its match. One of three states:
 * a wait after a local reveal of another name, "chosen before" when the database
 * says the match was revealed, or a plain confirmation.
 */
export const ConfirmNameCard = ({
  captureEvent,
  participant,
  deviceMemory,
  onConfirm,
  onCancel,
}: ConfirmNameCardProps) => {
  const { isWaiting, endsAt: waitEndsAt } = useNameChangeWait(deviceMemory, participant.id);
  const wasChosenBefore = exists(participant.matchViewedAt);
  const revealedCount = deviceMemory.revealedParticipantIds.length;

  useTrackEventOnce({
    isEnabled: isWaiting,
    onTrack: () => captureEvent(GroupNameWaitViewEvent, { revealedCount }),
  });

  return (
    <Card
      className="flex max-h-full max-w-xl flex-col overflow-hidden"
      bannerImage={lockNameImage}
      bannerImageAlt="Characters locking a name tag"
    >
      <div
        data-testid="confirm-name-step"
        className="animate-in fade-in flex min-h-0 flex-1 flex-col"
      >
        {isWaiting ? (
          <WaitContent waitEndsAt={waitEndsAt} revealedCount={revealedCount} onCancel={onCancel} />
        ) : (
          <ConfirmContent
            participantName={participant.name}
            wasChosenBefore={wasChosenBefore}
            onConfirm={() => {
              captureEvent(GroupNameConfirmEvent, { wasChosenBefore });
              onConfirm();
            }}
            onCancel={onCancel}
          />
        )}
      </div>
    </Card>
  );
};

const ConfirmContent = ({
  participantName,
  wasChosenBefore,
  onConfirm,
  onCancel,
}: {
  participantName: string;
  wasChosenBefore: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  return (
    <>
      <h1 className="mb-4 text-xl font-bold">
        {wasChosenBefore ? (
          'This name was chosen before'
        ) : (
          <>
            You&apos;re about to choose <DisplayParticipantName name={participantName} />
          </>
        )}
      </h1>

      <p className="text-muted-foreground mb-6">
        {wasChosenBefore
          ? 'You can still continue and see their match and wishlist.'
          : "You'll see their match and wishlist."}{' '}
        We&apos;ll remember your choice when you return to this page.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row-reverse">
        <Button variant="primary" className="min-w-0 sm:flex-1" onClick={onConfirm} type="button">
          <span className="truncate">
            I am <DisplayParticipantName name={participantName} />
          </span>
        </Button>
        <Button variant="secondary" className="sm:flex-none" onClick={onCancel} type="button">
          <span>Choose another name</span>
        </Button>
      </div>
    </>
  );
};

const WaitContent = ({
  waitEndsAt,
  revealedCount,
  onCancel,
}: {
  waitEndsAt: number;
  revealedCount: number;
  onCancel: () => void;
}) => {
  return (
    <>
      <h1 className="mb-4 text-xl font-bold">You can choose another name soon</h1>

      <p className="text-muted-foreground mb-6">
        You&apos;ve revealed {describeRevealCount(revealedCount)} on this device. You can choose
        another name once the countdown ends.
      </p>

      <div className="bg-primary/6 flex min-h-18 items-center justify-center rounded-2xl p-4 text-center">
        <WaitCountdown endsAt={waitEndsAt} />
      </div>

      <div className="mt-6 flex flex-col">
        <Button variant="secondary" onClick={onCancel} type="button">
          <span>Go back</span>
        </Button>
      </div>
    </>
  );
};

/** Ticks on its own so the rest of the card does not re-render every tick. */
const WaitCountdown = ({ endsAt }: { endsAt: number }) => {
  const remainingMs = useCountdown(endsAt);

  return (
    <p role="timer" aria-live="off">
      You can continue in <strong className="tabular-nums">{formatWait(remainingMs)}</strong>
    </p>
  );
};

function describeRevealCount(count: number): string {
  if (count === 1) return 'a match';
  if (count === 2) return 'matches twice';
  return `matches ${count} times`;
}
